import React, { useCallback, useEffect, useState } from 'react';
import { apiResendVerification, apiVerificationStatus } from '../services/api';
import { CONTACT_EMAIL } from '../constants/site';

const formatDelay = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

/**
 * Bouton « Renvoyer le code » avec compte à rebours.
 *
 * Le quota (nombre de renvois, délai entre deux) est appliqué par le serveur ;
 * ce composant n'en est que le miroir : il interroge /auth/verification-status
 * au montage puis décrémente localement, et se réaligne après chaque renvoi.
 */
const ResendVerificationCode = ({ email, onError }) => {
  const [retryAfter, setRetryAfter] = useState(null);   // null = état inconnu
  const [remaining, setRemaining] = useState(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const applyState = useCallback((state) => {
    setRetryAfter(state?.retry_after ?? 0);
    setRemaining(state?.resends_remaining ?? 0);
  }, []);

  useEffect(() => {
    if (!email) return undefined;
    let cancelled = false;
    apiVerificationStatus(email)
      .then((state) => { if (!cancelled) applyState(state); })
      .catch(() => { if (!cancelled) applyState({ retry_after: 0, resends_remaining: 1 }); });
    return () => { cancelled = true; };
  }, [email, applyState]);

  const counting = retryAfter > 0;
  useEffect(() => {
    if (!counting) return undefined;
    const id = setInterval(() => setRetryAfter((s) => (s > 1 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [counting]);

  const handleResend = async () => {
    setMessage('');
    onError?.('');
    setBusy(true);
    try {
      const state = await apiResendVerification(email);
      applyState(state);
      setMessage('Un nouveau code a été envoyé. Pensez à vérifier vos spams.');
    } catch (err) {
      onError?.(err.message);
    } finally {
      setBusy(false);
    }
  };

  // remaining === 0 couvre deux cas côté serveur : quota de renvois épuisé, ou
  // compte coach (aucun renvoi en libre-service, seul un admin peut réémettre).
  // Le message doit rester vrai dans les deux, d'où la formulation neutre.
  if (remaining === 0) {
    return (
      <p className="subtitle">
        Aucun renvoi n'est possible pour ce compte. Si vous n'avez toujours rien
        reçu, contactez le support à <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>
    );
  }

  const waiting = retryAfter === null || retryAfter > 0;

  return (
    <>
      {message && <p className="subtitle">{message}</p>}
      <div className="auth-link-row">
        <button
          type="button"
          className="auth-link-btn"
          onClick={handleResend}
          disabled={waiting || busy || !email}
        >
          {retryAfter > 0
            ? `Renvoyer le code (${formatDelay(retryAfter)})`
            : 'Renvoyer le code'}
        </button>
      </div>
    </>
  );
};

export default ResendVerificationCode;
